import { FC, FormEvent, MouseEventHandler, useCallback, useContext } from 'react';
import {
  LabelText,
  LoadingWrapper,
  NonMobileWrapper,
  TooltipWrapper,
  ValueText,
} from 'components/JaneModal/views/EditJaneView/EditJaneView.styled';
import {
  BKJTextInput,
  BKJButton,
  Flex,
  BKJComboBox,
  BKJLoadingSpinner,
  BKJCheckbox,
  BKJIconTooltip,
  BKJPillDropdown,
  BKJSelect,
  BKJPhoneNumberInput,
} from '@awais4123/fillify-design-library';
import { H3 } from 'components/JaneModal/views/EditJaneView/EditJaneView.styled';
import { ShiftDateCalendarInput } from 'components/ShiftDateCalendarInput';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { useFetchJaneByIdDataContext } from 'components/JaneModal/FetchJaneById.data.context';
import {
  EditJaneViewFormProvider,
  useEditJaneViewFormContext,
} from 'components/JaneModal/views/EditJaneView/EditJaneView.form.context';
import { BKJRadioInput } from 'components/BKJRadioInput/BKJRadioInput';
import { TeamLocationFilterInput } from 'components/JaneModal/views/EditJaneView/TeamLocationFilterInput';
import { SelectInputPositionByDepartment } from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment';
import {
  employmentStatuses,
  RenderOptions,
  monthToDaysMap,
  SeniorityLengthInput,
} from 'components/JaneModal/views/EditJaneView/EditJaneView.constants';
import { janeModalRoutingService } from 'components/JaneModal/JaneModal.constants';
import { isMultiLocationFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors/isMultiLocationFeatureEnabledSelector';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import { useSelector } from 'react-redux';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { SecondaryLocationSection } from 'components/JaneModal/views/EditJaneView/SecondaryLocationSection';
import { TeamMemberPageContext } from 'pages/TeamMemberPage/TeamMemberPage.context';
import { startCase } from 'utils/startCase';
import { isMultiLocationTieringFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { CommunitySeniorityMethodContext } from 'pages/TeamMemberPage/CommunitySeniorityMethod.context';
import { SeniorityMethodEnum } from 'types/graphql';
import { useTranslation } from 'hooks/useTranslation';

const Component: FC = () => {
  const {
    values: { location },
  } = useContext(TeamMemberPageContext);
  const {
    values,
    onChange,
    fieldErrors,
    apiErrors,
    isDisabled,
    isSubmitting,
    setSelectMonth,
    dayOptions,
    onSubmit,
    secondaryPositionsDropdownItems,
    numberOfPositionsDeleted,
    setShowDeactivateConfirmModal,
    showDeactivateConfirmModal,
    handlePrimaryPosition,
    isDiscardConfirmationOpen,
    setIsDiscardConfirmationOpen,
    handleRemovedSecondaryPosition,
    handleSecondaryPosition,
    handlePrimaryLocationChange,
    secondaryLocationsTieringSelection,
    setSecondaryLocationsTieringSelection,
  } = useEditJaneViewFormContext();

  const {
    data: {
      fetchCommunity: {
        organization: { seniorityMethod },
      },
    },
  } = useContext(CommunitySeniorityMethodContext);

  const { t } = useTranslation();

  const isMultiLocationFeatureEnabled = useSelector(
    isMultiLocationFeatureEnabledSelector(Number(location?.value)),
  );

  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(location?.value)),
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectMonth(e.target.value);
      if (values.birthDay) {
        values.birthDay = '';
      }
    },
    [setSelectMonth, values],
  );

  const handleTieringSelectionChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      const [key, location] = name.split('_');

      if (key === 'secondaryLocationsTieringSelection') {
        setSecondaryLocationsTieringSelection({
          ...secondaryLocationsTieringSelection,
          [location]: value,
        });
      }
      onChange(event);
    },
    [onChange, secondaryLocationsTieringSelection, setSecondaryLocationsTieringSelection],
  );

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
      padding="32px 40px 0"
      borderTop="1px solid rgba(139, 150, 163, 0.33)"
    >
      <BKJCenteredDialog
        isOpen={showDeactivateConfirmModal}
        variant="Danger"
        title={
          numberOfPositionsDeleted > 1
            ? t('team_member:Remove_Positions')
            : t('team_member:Remove_Position')
        }
        text={
          numberOfPositionsDeleted > 1
            ? t('team_member:Remove Position Details')
            : t('team_member:Remove Position Detail')
        }
        cancelLabel={t('auth:CANCEL')}
        actionLabel={t('team_member:Remove')}
        onAction={() => {
          handleRemovedSecondaryPosition({} as FormEvent<HTMLFormElement>);
        }}
        onCancel={() => setShowDeactivateConfirmModal(false)}
        onClose={() => setShowDeactivateConfirmModal(false)}
      />
      <Flex flexDirection="column" gap="32px" padding="0 0 50px" width="100%">
        <H3>{t('team_member:Edit Profile')}</H3>
        <BKJTextInput
          label={t('team_member:FIRST_NAME') + '*'}
          onChange={onChange}
          value={values?.firstName}
          name={'firstName'}
          autoComplete="off"
          error={fieldErrors.firstName}
        />
        <BKJTextInput
          label={t('team_member:LAST_NAME') + '*'}
          onChange={onChange}
          value={values?.lastName}
          name={'lastName'}
          autoComplete="off"
          error={fieldErrors.lastName}
        />
        <Flex flexDirection="column" gap="8px">
          <Flex alignItems="center">
            <LabelText>{t('team_member:EMAIL')}</LabelText>*
          </Flex>
          <ValueText style={{ marginLeft: '16px' }}>{values?.email}</ValueText>
        </Flex>
        <Flex flexDirection="column">
          <BKJPhoneNumberInput
            label={t('team_member:MOBILE_PHONE') + '*'}
            onChange={onChange}
            value={values?.phone}
            name={'phone'}
            autoComplete="off"
            error={fieldErrors.phone || apiErrors?.data?.error_messages?.phone}
            maxLength={11}
          />
          <UserGuard
            userType={[
              UserPermissionEnum.USER_TYPE_B2B,
              UserPermissionEnum.USER_TYPE_ESSENTIAL,
              UserPermissionEnum.USER_TYPE_PREMIUM,
            ]}
          >
            <Flex alignItems="center" gap="10px" style={{ marginTop: '10px', marginLeft: '16px' }}>
              <BKJCheckbox
                variant="PurpleSolid"
                value={values?.isMobile}
                name={'isMobile'}
                onChange={onChange}
              />
              <NonMobileWrapper>{t('team_member:FLAG_AS_NON_APP_USER')}</NonMobileWrapper>
              <BKJIconTooltip iconName="Info" placement="bottom">
                <TooltipWrapper>
                  {t('team_member:STAFF_WILL_RECEIVE_AUTOMATED_PHONE')}
                </TooltipWrapper>
              </BKJIconTooltip>
            </Flex>
          </UserGuard>
        </Flex>
        {isMultiLocationTieringEnabled && (
          <Flex flexDirection="column" gap="8px">
            <LabelText>{t('team_member:SENIORITY_LENGTH')}</LabelText>
            {seniorityMethod === SeniorityMethodEnum.years_months_days ? (
              <Flex padding="0 0 10px 0" gap="8px">
                <Flex>
                  <SeniorityLengthInput
                    name="seniorityYears"
                    onChange={onChange}
                    value={values.seniorityYears}
                    label={t('team_member:YEARS').toLowerCase()}
                    upperLimit={99}
                    placeholder={t('team_member:YEARS')}
                  />
                </Flex>
                <Flex>
                  <SeniorityLengthInput
                    name="seniorityMonths"
                    onChange={onChange}
                    value={values.seniorityMonths}
                    label={t('team_member:MONTHS').toLowerCase()}
                    upperLimit={11}
                    placeholder={t('team_member:MONTHS')}
                  />
                </Flex>
                <Flex>
                  <SeniorityLengthInput
                    name="seniorityDays"
                    onChange={onChange}
                    value={values.seniorityDays}
                    label={t('team_member:DAYS').toLowerCase()}
                    upperLimit={31}
                    placeholder={t('team_member:DAYS')}
                  />
                </Flex>
              </Flex>
            ) : (
              <Flex gap="10px">
                <SeniorityLengthInput
                  name="seniorityHours"
                  onChange={onChange}
                  value={values.seniorityHours}
                  label="hours"
                  placeholder="Hours"
                />
              </Flex>
            )}
          </Flex>
        )}
        {WhiteLabelAccessor.isPetalProvider && (
          <Flex flexDirection="column">
            <LabelText>{t('team_member:SELECT_A_LANGUAGE_PREFERENCE') + '*'}</LabelText>
            <BKJRadioInput
              name="locale"
              value="en"
              onChange={onChange}
              checked={values?.locale === 'en'}
              label={t('team_member:ENGLISH')}
            />
            <BKJRadioInput
              name="locale"
              value="fr"
              onChange={onChange}
              checked={values?.locale === 'fr'}
              label={t('team_member:FRENCH')}
            />
          </Flex>
        )}
        <Flex flexDirection="column" gap="8px">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {t('team_member:PRIMARY_LOCATION') + '*'}
          </LabelText>
          <TeamLocationFilterInput
            value={values?.location}
            onChange={handlePrimaryLocationChange}
            name="location"
            width="470px"
          />
        </Flex>
        {isMultiLocationFeatureEnabled && (
          <SecondaryLocationSection
            dropdownName="selectedSecondaryLocations"
            dropdownValue={values?.selectedSecondaryLocations}
            tieringName="secondaryLocationsTieringSelection"
            tieringValue={secondaryLocationsTieringSelection}
            tieringValueChange={handleTieringSelectionChange}
            onChange={onChange}
          />
        )}
        <Flex flexDirection="column" gap="8px">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {t('team_member:PRIMARY_POSITION') + '*'}
          </LabelText>
          <SelectInputPositionByDepartment
            dropdownVariant="Square"
            placeholder={t('team_member:SELECT_POSITION')}
            onChange={handlePrimaryPosition}
            type="Single"
            name="position_id"
            variant="GreyOutlined"
            value={values?.position_id}
            error={fieldErrors?.position_id}
            width="100%"
            key={`${values?.location?.value}`}
            allocatedToCommunities={[`${values?.location?.value}`]}
            dropdownProps={{
              dropdownListItemVariant: 'List',
              isSearchable: false,
            }}
          />
        </Flex>
        <Flex flexDirection="column">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {t('team_member:SECONDARY_POSITIONS')}
          </LabelText>
          <BKJPillDropdown
            onChange={handleSecondaryPosition}
            name="selectedSecondaryPositions"
            value={values?.selectedSecondaryPositions}
            key={`${values?.location?.value}`}
            options={secondaryPositionsDropdownItems}
            validations={[]}
            error={fieldErrors.selectedSecondaryPositions}
            placeholder={t('team_member:SELECT_POSITIONS')}
            max={secondaryPositionsDropdownItems?.length || 0}
          />
        </Flex>
        <UserGuard
          userType={[
            UserPermissionEnum.USER_TYPE_B2B,
            UserPermissionEnum.USER_TYPE_ESSENTIAL,
            UserPermissionEnum.USER_TYPE_PREMIUM,
          ]}
        >
          <BKJComboBox
            label={t('team_member:EMPLOYMENT_STATUS') + '*'}
            placeholder={t('team_member:SELECT_STATUS')}
            name="employmentStatus"
            value={[startCase(values?.employmentStatus)]}
            onChange={onChange}
            options={employmentStatuses()}
            type="Single"
            action="OpenToggle"
            filter="TimeStartsWith"
            error={fieldErrors?.employmentStatus}
          />
          <BKJTextInput
            label={t('team_member:HOURLY_RATE')}
            onChange={onChange}
            value={`${values?.hourlyRate ? values?.hourlyRate : ''}`}
            name={'hourlyRate'}
            autoComplete="off"
          />
        </UserGuard>
        <BKJTextInput
          label={t('team_member:EMPLOYEE_ID')}
          onChange={onChange}
          value={values?.employeeId}
          name={'employeeId'}
          autoComplete="off"
          error={fieldErrors?.employeeId}
        />
        <Flex flexDirection="column" gap="8px">
          <LabelText>{t('team_member:EMPLOYEE_START_DATE')}</LabelText>
          <ShiftDateCalendarInput
            name="employeeStartDate"
            value={values?.employeeStartDate}
            onChange={onChange}
            width="100%"
            error={fieldErrors?.employeeStartDate}
          />
        </Flex>
        <Flex gap="16px">
          <Flex flexDirection="column" width="100%">
            <LabelText>{t('team_member:BIRTH_MONTH')}</LabelText>
            <BKJSelect
              placeholder={t('team_member:SELECT_MONTH')}
              name="birthMonth"
              options={Object.keys(monthToDaysMap).map((month, index) => ({
                key: index,
                id: index,
                value: month,
                label: t(`team_member:${month}`),
              }))}
              onChange={(e) => {
                handleChange(e);
                onChange(e);
              }}
              type="Single"
              variant="GreyOutlined"
              value={[`${values?.birthMonth}`]}
              renderOptions={RenderOptions}
              placeholderColor="TextDisabled"
              error={fieldErrors.birthMonth}
            />
          </Flex>
          <Flex flexDirection="column" width="100%">
            <LabelText>{t('team_member:BIRTH_DAY')}</LabelText>
            <BKJSelect
              placeholder={t('team_member:DAY')}
              name="birthDay"
              options={dayOptions}
              onChange={(e) => {
                onChange(e);
              }}
              type="Single"
              variant="GreyOutlined"
              value={[`${values?.birthDay}`]}
              renderOptions={RenderOptions}
              placeholderColor="TextDisabled"
              error={fieldErrors.birthDay}
            />
          </Flex>
        </Flex>
      </Flex>

      <BKJSideModalFooter>
        <Flex width="100%" justifyContent="end">
          <BKJButton
            variant="PurpleOutlined"
            width="140px"
            onClick={() => {
              setIsDiscardConfirmationOpen(true);
              janeModalRoutingService?.isOpen();
            }}
            type="button"
          >
            {t('team_member:CANCEL')}
          </BKJButton>
          <BKJButton
            variant="PurpleSolid"
            disabled={isDisabled || isSubmitting}
            isLoading={isSubmitting}
            width="140px"
            onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
            type="button"
            style={{ marginLeft: '4%' }}
          >
            {t('auth:SAVE')}
          </BKJButton>
        </Flex>
      </BKJSideModalFooter>
      <BKJCenteredDialog
        isOpen={isDiscardConfirmationOpen}
        variant="Danger"
        title={t('create_shifts:DISCARD_CHANGES')}
        data-cy="discard-modal"
        text=""
        cancelLabel={t('auth:CANCEL')}
        actionLabel={t('create_shifts:DISCARD')}
        onAction={() => {
          setIsDiscardConfirmationOpen(false);
          return janeModalRoutingService.goToPrevView();
        }}
        onCancel={() => setIsDiscardConfirmationOpen(false)}
        onClose={() => setIsDiscardConfirmationOpen(false)}
      />
    </Flex>
  );
};

export const EditJaneDefaultFormComponent: FC = () => {
  const { isLoading } = useFetchJaneByIdDataContext();
  if (isLoading)
    return (
      <LoadingWrapper>
        <BKJLoadingSpinner variant="Default" />
      </LoadingWrapper>
    );
  return (
    <EditJaneViewFormProvider>
      <Component />
    </EditJaneViewFormProvider>
  );
};
export const EditJaneView: FC = () => {
  return <EditJaneDefaultFormComponent />;
};
